import { LitElement, css, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import '@shoelace-style/shoelace/dist/components/button/button.js';
import '@shoelace-style/shoelace/dist/components/badge/badge.js';
import '@shoelace-style/shoelace/dist/components/icon/icon.js';
import '@shoelace-style/shoelace/dist/components/alert/alert.js';
import '@shoelace-style/shoelace/dist/components/dropdown/dropdown.js';
import '@shoelace-style/shoelace/dist/components/menu/menu.js';
import '@shoelace-style/shoelace/dist/components/menu-item/menu-item.js';
import { WithUserProfileConsumer } from '../mixins/with-user-profile-consumer';
import { WithActiveShopConsumer } from '../mixins/with-active-shop-consumer';
import { ApiFactory } from '../api/easypos-api';
import { IStockTake, StockTakeKind } from '../api/model/requests';
import { resolveRouterPath, router } from '../router';
import { styles as sharedStyles } from '../styles/shared-styles';
import '../components/data-table';
import {
  createArrayDataProvider,
  type DataTableColumnDef,
} from '../components/data-table';

const Base = WithActiveShopConsumer(WithUserProfileConsumer(LitElement));

const STATUS_VARIANT: Record<
  IStockTake['status'],
  'neutral' | 'primary' | 'warning' | 'success' | 'danger'
> = {
  in_progress: 'primary',
  completed: 'success',
  cancelled: 'neutral',
};

const KIND_VARIANT: Record<
  StockTakeKind,
  'neutral' | 'primary' | 'warning' | 'success' | 'danger'
> = {
  opening: 'warning',
  closing: 'primary',
  adhoc: 'neutral',
};

// Sub-phase U.2a.3 — stock takes as a sortable table. Row click →
// /stocktake/:id. The Start dropdown + in-progress alert stay in the
// header. F5-resistant _initStarted init pattern.
@customElement('stocktake-list-page')
export class StocktakeListPage extends Base {
  @state() private _takes: IStockTake[] = [];
  @state() private _loading = true;
  @state() private _error: string | null = null;
  @state() private _starting = false;

  private _initStarted = false;

  static styles = [
    sharedStyles,
    css`
      :host {
        display: flex;
        flex-direction: column;
        height: 100%;
        min-height: 0;
        padding: 16px;
        max-width: 1200px;
        margin: 0 auto;
        box-sizing: border-box;
        width: 100%;
        gap: 12px;
      }
      header.bar {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 8px;
        flex-wrap: wrap;
        flex: none;
      }
      app-data-table {
        flex: 1 1 auto;
        min-height: 0;
      }
      sl-alert {
        flex: none;
      }
      .empty {
        text-align: center;
        padding: 32px 16px;
        color: var(--sl-color-neutral-500);
      }
      .money {
        font-variant-numeric: tabular-nums;
      }
    `,
  ];

  firstUpdated() {
    if (this.activeShop) void this._init();
  }

  updated(changed: Map<string, unknown>) {
    if (changed.has('activeShop') && this.activeShop && !this._initStarted) {
      void this._init();
    }
  }

  private async _init() {
    if (this._initStarted) return;
    this._initStarted = true;
    await this._load();
  }

  private async _load() {
    if (!this.activeShop) {
      this._error = 'No active shop.';
      this._loading = false;
      return;
    }
    this._loading = true;
    this._error = null;
    const api = ApiFactory.authenticated();
    const r = await api.listStockTakes(this.activeShop.shopName);
    if (!r.success || !r.data) {
      this._error = r.error ?? 'Failed to load stock takes.';
    } else {
      this._takes = r.data;
    }
    this._loading = false;
  }

  private async _start(kind: StockTakeKind) {
    if (!this.activeShop || this._starting) return;
    this._starting = true;
    this._error = null;
    const api = ApiFactory.authenticated();
    const r = await api.startStockTake(this.activeShop.shopName, { kind });
    this._starting = false;
    if (!r.success || !r.data) {
      this._error = r.error ?? 'Failed to start stock take.';
      return;
    }
    router.navigate(
      resolveRouterPath(`stocktake/${encodeURIComponent(r.data.id)}`)
    );
  }

  private _open(t: IStockTake) {
    router.navigate(resolveRouterPath(`stocktake/${encodeURIComponent(t.id)}`));
  }

  // Owner-only Start dropdown — three explicit kinds. Each triggers a
  // POST /stocktake with the corresponding kind. Opening fails clean if
  // there's no prior completed closing; the operator can pick adhoc /
  // closing and try again, or run a closing first.
  private _renderStartDropdown() {
    return html`
      <sl-dropdown>
        <sl-button
          slot="trigger"
          variant="primary"
          size="small"
          caret
          ?loading=${this._starting}
        >
          <sl-icon slot="prefix" name="clipboard-check"></sl-icon>
          Start
        </sl-button>
        <sl-menu
          @sl-select=${(e: CustomEvent<{ item: { value: string } }>) => {
            const kind = e.detail.item.value as StockTakeKind;
            void this._start(kind);
          }}
        >
          <sl-menu-item value="closing">Closing — end of day</sl-menu-item>
          <sl-menu-item value="opening"
            >Opening — verify against prior closing</sl-menu-item
          >
          <sl-menu-item value="adhoc"
            >Adhoc — quick count any time</sl-menu-item
          >
        </sl-menu>
      </sl-dropdown>
    `;
  }

  private get _dataProvider() {
    return createArrayDataProvider<IStockTake>(() => this._takes, {
      filterFn: (item, text) =>
        item.kind.toLowerCase().includes(text) ||
        item.status.toLowerCase().includes(text) ||
        (item.note ?? '').toLowerCase().includes(text),
    });
  }

  private _columns(): DataTableColumnDef[] {
    return [
      {
        title: 'Status',
        fieldName: 'status',
        sortable: true,
        width: '120px',
        displayValue: (_t, take: IStockTake) =>
          html`<sl-badge variant=${STATUS_VARIANT[take.status]} pill
            >${take.status.replace('_', ' ')}</sl-badge
          >`,
      },
      {
        title: 'Kind',
        fieldName: 'kind',
        sortable: true,
        width: '100px',
        displayValue: (_t, take: IStockTake) =>
          html`<sl-badge variant=${KIND_VARIANT[take.kind]} pill
            >${take.kind}</sl-badge
          >`,
      },
      {
        title: 'Started',
        fieldName: 'startedUtc',
        sortable: true,
        width: '180px',
        isDefaultSortDirectionAscending: false,
        displayValue: (_t, take: IStockTake) =>
          new Date(take.startedUtc).toLocaleString(undefined, {
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          }),
      },
      {
        title: 'Completed',
        fieldName: 'completedUtc',
        sortable: true,
        width: '180px',
        displayValue: (_t, take: IStockTake) =>
          take.completedUtc
            ? new Date(take.completedUtc).toLocaleString(undefined, {
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
              })
            : html`<span style="opacity: 0.5;">—</span>`,
      },
      {
        title: 'Counted',
        fieldName: 'lines',
        width: '110px',
        align: 'right',
        classes: 'money',
        displayValue: (_t, take: IStockTake) => {
          const total = take.lines.length;
          const counted = take.lines.filter((l) => l.counts.length > 0).length;
          return `${counted} / ${total}`;
        },
      },
      {
        title: 'Note',
        fieldName: 'note',
        minWidth: '160px',
        displayValue: (_t, take: IStockTake) =>
          take.note
            ? html`<span style="font-size: 12px;">${take.note}</span>`
            : html`<span style="opacity: 0.5;">—</span>`,
      },
    ];
  }

  render() {
    const isOwner =
      this.activeShop &&
      this.userProfile?.hasRole(this.activeShop.shopName, 'owner');
    const inProgress = this._takes.find((t) => t.status === 'in_progress');

    return html`
      <header class="bar">
        <h2 style="margin: 0;">Stock takes</h2>
        ${isOwner && !inProgress ? this._renderStartDropdown() : nothing}
      </header>

      ${
        this._error
          ? html`<sl-alert
              variant="danger"
              open
              closable
              @sl-after-hide=${() => (this._error = null)}
              >${this._error}</sl-alert
            >`
          : nothing
      }
      ${
        inProgress
          ? html`
              <sl-alert variant="primary" open style="margin-bottom: 12px;">
                A stock take is in progress. Continue counting or cancel it
                before starting a new one.
              </sl-alert>
            `
          : nothing
      }
      ${
        this._loading
          ? html`<p>Loading…</p>`
          : this._takes.length === 0
            ? html`
                <div class="empty">
                  <p>No stock takes yet.</p>
                  ${isOwner ? this._renderStartDropdown() : nothing}
                </div>
              `
            : html`
                <app-data-table
                  fill-height
                  clickrows
                  .columns=${this._columns()}
                  .dataProvider=${this._dataProvider}
                  .keyevent=${(take: IStockTake) => take.id}
                  tablestyle="standardtable"
                  @rowclick=${(e: CustomEvent<{ item: IStockTake }>) =>
                    this._open(e.detail.item)}
                ></app-data-table>
              `
      }
    `;
  }
}
